import React, { memo, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { buildOverviewItemId } from 'site-modules/personalized-search/utils/build-overview-item-id';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { SearchRenderer } from 'site-modules/personalized-search/search-renderer/search-renderer';
import { OverviewContext } from 'site-modules/personalized-search/context/overview-context/overview-context';

import 'client/site-modules/personalized-search/styles.scss';
import './collapsible-overview-item.scss';

const transformComponentParams = componentParams => {
  const { components } = componentParams;
  const collapsibleComponentName = components[0];
  return {
    collapsibleComponentParams: {
      components: [collapsibleComponentName],
      [collapsibleComponentName]: componentParams[collapsibleComponentName],
    },
    restComponentsParams: {
      ...componentParams,
      components: components.filter(componentName => componentName !== collapsibleComponentName),
    },
  };
};

export const CollapsibleOverviewItem = memo(({ componentParams, parentComponentName, componentPath, ...restProps }) => {
  const { restComponentsParams, collapsibleComponentParams } = useMemo(
    () => transformComponentParams(componentParams),
    [componentParams]
  );

  const { make, model, year, trim } = componentParams.header;

  const { expandedItems, toggleItem } = useContext(OverviewContext);

  const id = buildOverviewItemId({ year, make, model, trim });

  const onToggle = useCallback(() => {
    toggleItem(id, !expandedItems[id]);
  }, [expandedItems, id, toggleItem]);

  const buttonAriaLabel = `${year} ${make?.name} ${model?.name} overview`;

  const renderCollapseControl = useCallback(
    ({ isOpen }) => {
      return (
        <div className="d-flex align-items-center justify-content-between p-0 pos-r">
          <SearchRenderer
            componentParams={collapsibleComponentParams}
            parentComponentName={parentComponentName}
            componentPath={componentPath}
            {...restProps}
            trackActionStart={isOpen}
          />
          <Button
            className="collapsed-button p-0"
            color="link"
            onClick={onToggle}
            data-no-refresh
            aria-expanded={isOpen}
            data-tracking-ignore-value
            aria-label={buttonAriaLabel}
          >
            <span
              className={classnames('text-cool-gray-40 size-14', isOpen ? 'icon-arrow-up3' : 'icon-arrow-down3')}
              aria-hidden
            />
          </Button>
        </div>
      );
    },
    [collapsibleComponentParams, parentComponentName, restProps, componentPath, onToggle, buttonAriaLabel]
  );

  return (
    <>
      <ContentCollapse
        isOpen={expandedItems[id]}
        controlled
        btnContainerClasses="me-0 ms-0"
        isEclipseFade={false}
        renderCollapseControl={renderCollapseControl}
        trackingId="toggle_overview"
      >
        <div>
          <SearchRenderer
            componentParams={restComponentsParams}
            parentComponentName={parentComponentName}
            componentPath={componentPath}
            {...restProps}
          />
        </div>
      </ContentCollapse>
      <hr />
    </>
  );
});

CollapsibleOverviewItem.displayName = 'CollapsibleOverviewItem';

CollapsibleOverviewItem.propTypes = {
  componentParams: PropTypes.shape({
    header: PropTypes.shape({
      make: PropTypes.shape({
        name: PropTypes.string,
      }),
      model: PropTypes.shape({
        name: PropTypes.string,
      }),
      year: PropTypes.string,
      trim: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  }).isRequired,
  parentComponentName: PropTypes.string.isRequired,
  componentPath: PropTypes.string.isRequired,
};
